/** What a cycle's chart draws, built from the API's own figures only. */
import type { EChartsCoreOption } from "echarts/core";

import type { CandleResponse, GridLevelResponse } from "@/shared/api";
import { finiteGeometryValue } from "@/shared/money/geometry";

export type CyclePalette = {
  readonly background: string;
  readonly border: string;
  readonly grid: string;
  readonly fontMono: string;
  readonly fontSans: string;
  readonly negative: string;
  readonly positive: string;
  readonly info: string;
  readonly warning: string;
  readonly text: string;
  readonly muted: string;
};

export type CycleMarks = {
  readonly openedAt: string;
  readonly levels: readonly GridLevelResponse[];
  /** The average the position paid. */
  readonly average: string | null;
  /** Where the exit arms: the target. */
  readonly target: string | null;
  /** Where the trail sells now, only while trailing. */
  readonly trigger: string | null;
  /** The last price, tagged on the axis like the rest. */
  readonly current?: string | null;
};

type Line = {
  readonly value: number;
  readonly label: string;
  readonly color: string;
  readonly type: "solid" | "dashed" | "dotted";
  readonly width: number;
  readonly named: boolean;
  /** How much of the line shows; the ladder sits behind the named prices. */
  readonly opacity?: number;
  /** The exact price as the API sent it, for the axis tag. */
  readonly text: string;
};

/** A price as a coordinate, through the one sanctioned conversion. */
function number(text: string | null | undefined): number | null {
  if (text == null) return null;
  const value = finiteGeometryValue(text);
  return value !== null && value > 0 ? value : null;
}

function coordinate(text: string): number {
  return finiteGeometryValue(text) ?? Number.NaN;
}

/** Every horizontal line, in the order it is drawn (later lines on top). */
export function cycleLines(marks: CycleMarks, palette: CyclePalette): readonly Line[] {
  const lines: Line[] = [];
  for (const level of marks.levels) {
    if (level.state === "CANCELLED") continue;
    const price = number(level.weighted_fill_price ?? level.planned_order_price);
    if (price === null) continue;
    const filled = level.state === "FILLED" || level.state === "PARTIALLY_FILLED";
    lines.push({
      value: price,
      label: filled ? "куплено" : "ордер",
      color: palette.muted,
      type: filled ? "solid" : "dashed",
      // The ladder is the background of the picture, not its subject.
      width: 0.6,
      named: false,
      opacity: filled ? 0.55 : 0.3,
      text: level.weighted_fill_price ?? level.planned_order_price ?? "",
    });
  }
  const named: ReadonlyArray<readonly [string | null | undefined, string, string, Line["type"]]> = [
    [marks.average, "Средняя", palette.text, "solid"],
    [marks.target, "Цель", palette.positive, "dashed"],
    [marks.trigger, "Трейлинг", palette.warning, "dotted"],
    [marks.current, "Сейчас", palette.info, "dotted"],
  ];
  for (const [text, label, color, type] of named) {
    const value = number(text);
    if (value !== null && text != null) {
      lines.push({ value, label, color, type, width: label === "Сейчас" ? 1 : 2, named: true, text });
    }
  }
  return lines;
}

/**
 * Where each axis tag sits, in pixels from the top: on its own line, unless
 * that would cover the tag above it, in which case it steps down just clear.
 */
export function spreadTags(pixels: readonly number[], gap = 18): number[] {
  const order = pixels.map((y, index) => ({ y, index })).sort((a, b) => a.y - b.y);
  const placed = pixels.map(() => 0);
  let last = Number.NEGATIVE_INFINITY;
  for (const { y, index } of order) {
    const at = Math.max(y, last + gap);
    placed[index] = at;
    last = at;
  }
  return placed;
}

export function buildCycleChartOption(
  bars: readonly CandleResponse[],
  marks: CycleMarks,
  palette: CyclePalette,
  /** A narrow chart tags the price alone; the colour says which line. */
  compact = false,
): EChartsCoreOption {
  const lines = cycleLines(marks, palette);
  const prices = [
    ...bars.flatMap((bar) => [coordinate(bar.low), coordinate(bar.high)]),
    ...lines.map((line) => line.value),
  ].filter((value) => Number.isFinite(value) && value > 0);
  const low = prices.length === 0 ? 0 : Math.min(...prices);
  const high = prices.length === 0 ? 1 : Math.max(...prices);
  const pad = (high - low) * 0.04 || high * 0.02;
  const opened = Date.parse(marks.openedAt);
  return {
    animation: false,
    aria: { enabled: true, label: { description: "Свечной график цикла с ордерами и целями" } },
    grid: { left: 8, right: compact ? 76 : 124, top: 22, bottom: 28, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross" },
      backgroundColor: palette.background,
      borderColor: palette.border,
      textStyle: { color: palette.text, fontFamily: palette.fontSans },
    },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: palette.border } },
      axisLabel: { color: palette.muted, fontFamily: palette.fontMono, hideOverlap: true, formatter: "{dd}.{MM}" },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      // Prices on the left: the right edge carries the named lines' labels,
      // which a right-hand axis would print over on a phone.
      position: "left",
      scale: true,
      min: low - pad,
      max: high + pad,
      // The padded edges are not prices anyone set: no label on them, and
      // the rest at four significant digits, as a trader reads a quote.
      axisLabel: {
        color: palette.muted,
        fontFamily: palette.fontMono,
        showMinLabel: false,
        showMaxLabel: false,
        formatter: (value: number) => {
          const text = value.toPrecision(4);
          return text.includes(".") && !text.includes("e") ? text.replace(/\.?0+$/, "") : text;
        },
      },
      splitLine: { lineStyle: { color: palette.grid, type: "dashed" } },
    },
    dataZoom: [{ type: "inside" }],
    series: [
      {
        type: "candlestick",
        name: "Цена",
        // ECharts reads [open, close, low, high].
        data: bars.map((bar) => [
          Date.parse(bar.at),
          coordinate(bar.open),
          coordinate(bar.close),
          coordinate(bar.low),
          coordinate(bar.high),
        ]),
        encode: { x: 0, y: [1, 2, 3, 4] },
        itemStyle: {
          color: palette.positive,
          color0: palette.negative,
          borderColor: palette.positive,
          borderColor0: palette.negative,
        },
        markLine: {
          symbol: "none",
          silent: true,
          data: [
            ...lines.map((line) => ({
              yAxis: line.value,
              lineStyle: { color: line.color, type: line.type, width: line.width, opacity: line.opacity ?? 1 },
              // Tags are drawn over the chart by the component, where they can
              // step clear of each other; the line itself carries no label.
              label: { show: false },
            })),
            ...(Number.isFinite(opened)
              ? [
                  {
                    xAxis: opened,
                    lineStyle: { color: palette.muted, type: "dashed", width: 1 },
                    label: { show: true, formatter: "Вход", color: palette.muted },
                  },
                ]
              : []),
          ],
        },
      },
    ],
  };
}
